import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { TrendingUp } from "lucide-react";
import type { TrendDataPoint, DateRange } from "@/hooks/use-analytics-data";

interface TransactionTrendsChartProps {
  data: TrendDataPoint[];
  dateRange: DateRange;
}

const RANGE_LABELS: Record<DateRange, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
};

function parseDateString(dateStr: string): Date {
  const dateOnly = dateStr.split(" ")[0];
  return new Date(dateOnly + "T00:00:00");
}

function formatDateLabel(dateStr: string, range: DateRange): string {
  const date = parseDateString(dateStr);
  if (range === "7d") {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }
  if (range === "90d") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number; color: string; dataKey: string }>;
  label?: string;
}) {
  if (!active || !payload || !label) return null;

  const date = parseDateString(label);
  const formattedDate = date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="rounded-lg border border-border bg-surface p-3 shadow-lg">
      <p className="text-xs font-medium text-foreground mb-2">
        {formattedDate}
      </p>
      <div className="space-y-1">
        {payload.map((entry) => (
          <div
            key={entry.dataKey}
            className="flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-2">
              <div
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-xs text-muted-foreground capitalize">
                {entry.dataKey}
              </span>
            </div>
            <span className="text-xs font-semibold tabular-nums">
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TransactionTrendsChart({
  data,
  dateRange,
}: TransactionTrendsChartProps) {
  const hasData = data.some((d) => d.receipts > 0 || d.dispenses > 0);

  // Calculate totals for the period
  const totals = data.reduce(
    (acc, d) => ({
      receipts: acc.receipts + d.receipts,
      dispenses: acc.dispenses + d.dispenses,
    }),
    { receipts: 0, dispenses: 0 }
  );

  // Calculate tick interval based on range
  const tickInterval = dateRange === "7d" ? 0 : dateRange === "30d" ? 4 : 13;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Transaction Trends</CardTitle>
            <CardDescription>{RANGE_LABELS[dateRange]}</CardDescription>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>
              <span className="font-semibold text-emerald-600 tabular-nums">
                {totals.receipts}
              </span>{" "}
              receipts
            </span>
            <span>
              <span className="font-semibold text-blue-600 tabular-nums">
                {totals.dispenses}
              </span>{" "}
              dispenses
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="flex flex-col items-center justify-center h-[300px] text-center">
            <div className="mb-3 rounded-full bg-muted p-3">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">
              No transaction data
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Transactions will appear here as they are recorded
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart
              data={data}
              margin={{ top: 5, right: 10, left: -10, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorReceipts" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#059669" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorDispenses" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                className="stroke-border/50"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                className="fill-muted-foreground"
                tickFormatter={(value) => formatDateLabel(value, dateRange)}
                interval={tickInterval}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                className="fill-muted-foreground"
                allowDecimals={false}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
              />
              <Legend
                verticalAlign="top"
                height={36}
                iconType="circle"
                iconSize={8}
                formatter={(value: string) => (
                  <span className="text-xs text-muted-foreground capitalize">
                    {value}
                  </span>
                )}
              />
              <Area
                type="monotone"
                dataKey="receipts"
                stroke="#059669"
                strokeWidth={2}
                fill="url(#colorReceipts)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: "#fff" }}
              />
              <Area
                type="monotone"
                dataKey="dispenses"
                stroke="#2563eb"
                strokeWidth={2}
                fill="url(#colorDispenses)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: "#fff" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
